import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { bindInventoryLiveUpdates } from './inventory-live-updates';
import { Router, RouterLink } from '@angular/router';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { InventoryPageHintComponent } from './inventory-page-hint.component';
import { CatalogOffering, CatalogService, CatalogWarehouse, CatalogWarehouseBin, InboundShipment } from './catalog.service';

@Component({
  standalone: true,
  selector: 'ain-inventory-inbound-shipments',
  imports: [AinPageComponent, AinStateComponent, InventoryPageHintComponent, RouterLink, FormsModule, ButtonModule, InputTextModule, SelectModule, TableModule],
  template: `
    <ain-page [title]="t('catalog.inbound.title')" [description]="t('catalog.inbound.description')">
      <ain-inventory-page-hint pageKey="inboundShipments" />
      @if (loading()) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else {
        <section class="form-card">
          <h3>{{ t('catalog.inbound.createTitle') }}</h3>
          <div class="form-grid">
            <label><span>{{ t('catalog.inventory.reference') }}</span><input pInputText [(ngModel)]="form.referenceNumber" /></label>
            <label><span>{{ t('catalog.inbound.externalReference') }}</span><input pInputText [(ngModel)]="form.externalReference" /></label>
            <label><span>{{ t('catalog.inventory.warehouseId') }}</span><p-select [(ngModel)]="form.warehouseId" [options]="warehouseOptions()" optionLabel="label" optionValue="value" [filter]="true" filterBy="label" appendTo="body" (ngModelChange)="onWarehouseChange()" /></label>
            <label><span>{{ t('catalog.inbound.stagingBin') }}</span><p-select [(ngModel)]="form.stagingBinId" [options]="binOptions()" optionLabel="label" optionValue="value" [filter]="true" filterBy="label" appendTo="body" showClear="true" /></label>
            <label><span>{{ t('catalog.inbound.dockDoor') }}</span><input pInputText [(ngModel)]="form.dockDoorCode" /></label>
            <label><span>{{ t('catalog.inventory.offeringId') }}</span><p-select [(ngModel)]="form.offeringId" [options]="offeringOptions()" optionLabel="label" optionValue="value" [filter]="true" filterBy="label" appendTo="body" /></label>
            <label><span>{{ t('catalog.inbound.expectedQty') }}</span><input pInputText type="number" min="0" step="0.001" [(ngModel)]="form.expectedQuantity" /></label>
          </div>
          <button pButton type="button" [label]="t('catalog.action.create')" (click)="create()"></button>
        </section>

        <section class="list-card">
          <p-table [value]="shipments()" [paginator]="true" [rows]="15">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('catalog.inventory.reference') }}</th>
                <th>{{ t('columns.status') }}</th>
                <th>{{ t('catalog.inbound.externalReference') }}</th>
                <th></th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.referenceNumber }}</td>
                <td>{{ row.status }}</td>
                <td>{{ row.externalReference || '—' }}</td>
                <td><a [routerLink]="['/inventory/inbound-shipments', row.id]">{{ t('common.open') }}</a></td>
              </tr>
            </ng-template>
          </p-table>
        </section>
        @if (error()) { <p class="error">{{ error() }}</p> }
      }
    </ain-page>
  `,
  styles: [`
    .form-card, .list-card { border: 1px solid #e2e8f0; border-radius: 12px; padding: 12px; margin-bottom: 12px; background: #fff; }
    .form-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 10px; }
    .form-grid label { display: grid; gap: 6px; }
    .error { color: #b91c1c; }
  `]
})
export class InventoryInboundShipmentsComponent implements OnInit {
  private readonly service = inject(CatalogService);
  private readonly localization = inject(LocalizationService);
  private readonly router = inject(Router);
  private readonly liveUpdateService = inject(LiveUpdateService);
  private readonly contextService = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);

  readonly loading = signal(true);
  readonly error = signal('');
  readonly shipments = signal<InboundShipment[]>([]);
  readonly offerings = signal<CatalogOffering[]>([]);
  readonly warehouses = signal<CatalogWarehouse[]>([]);
  readonly bins = signal<CatalogWarehouseBin[]>([]);

  readonly form = {
    referenceNumber: '',
    externalReference: '',
    warehouseId: '',
    stagingBinId: null as string | null,
    dockDoorCode: '',
    offeringId: '',
    expectedQuantity: 0
  };

  warehouseOptions = () => this.warehouses().map(w => ({ label: `${w.code} — ${w.name}`, value: w.id }));
  offeringOptions = () => this.offerings().filter(o => o.inventoryEnabled).map(o => ({ label: `${o.code} — ${o.title}`, value: o.id }));
  binOptions = () => this.bins().map(b => ({ label: `${b.code} — ${b.name}`, value: b.id }));

  t(key: string): string {
    return this.localization.translate(key);
  }

  ngOnInit(): void {
    bindInventoryLiveUpdates(
      { liveUpdateService: this.liveUpdateService, contextService: this.contextService, destroyRef: this.destroyRef },
      () => void this.load()
    );
    void this.load();
  }

  onWarehouseChange(): void {
    this.form.stagingBinId = null;
    if (!this.form.warehouseId) {
      this.bins.set([]);
      return;
    }
    this.service.listWarehouseBins(this.form.warehouseId).subscribe({
      next: bins => this.bins.set(bins),
      error: () => this.bins.set([])
    });
  }

  create(): void {
    this.error.set('');
    if (!this.form.referenceNumber.trim() || !this.form.warehouseId || !this.form.offeringId || this.form.expectedQuantity <= 0) {
      this.error.set(this.t('catalog.inbound.requiredFields'));
      return;
    }
    this.service.createInboundShipment({
      warehouseId: this.form.warehouseId,
      stagingBinId: this.form.stagingBinId,
      referenceNumber: this.form.referenceNumber.trim(),
      externalReference: this.form.externalReference.trim() || null,
      scheduledArrivalUtc: null,
      dockDoorCode: this.form.dockDoorCode.trim() || null,
      lines: [{ offeringId: this.form.offeringId, expectedQuantity: this.form.expectedQuantity }]
    }).subscribe({
      next: shipment => void this.router.navigate(['/inventory/inbound-shipments', shipment.id]),
      error: () => this.error.set(this.t('catalog.inbound.createFailed'))
    });
  }

  private async load(): Promise<void> {
    this.loading.set(true);
    this.service.listInboundShipments().subscribe({
      next: list => {
        this.shipments.set(list);
        this.loading.set(false);
      },
      error: () => {
        this.error.set(this.t('catalog.inbound.loadFailed'));
        this.loading.set(false);
      }
    });
    this.service.listOfferings().subscribe({ next: o => this.offerings.set(o) });
    this.service.listWarehouses().subscribe({ next: w => this.warehouses.set(w) });
  }
}
